import type { RecipeV2Doc } from '@ctcells/api'
import { t, type MessageKey } from '@ctcells/i18n'

/**
 * 레시피 문서 안의 배치(랩웨어 · 시약 · 시료 자리)를 표 · 그림 재료로 편다 — 값은 레시피 문서 · 서버 요약 그대로다.
 * 🔴 다른 템플릿(작업 · 데크)과 잇거나 견주지 않는다 — 그 템플릿 화면은 걷었다(eric 2026-10-26 「응.. 다 빼줘」).
 */

type Obj = Record<string, unknown>
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v)

/* ── 레시피 안의 배치(랩웨어 · 시약 · 시료 자리) ─────────────────────────────────────────────── */

/**
 * 표준 레시피 판에 «데크» 없이 레시피 데이터를 바로(eric 2026-10-13 「데크라는 거 빼고 데이터만 보여줄래? 표준 레시피에 포함되어 있으면」).
 * 출처는 문서의 적재 명령 — 랩웨어 = `loadLabware`(칸 · 이름 · 자리) · 시약 = `loadLiquid` 중 시료가 아닌 것(자리 · 웰 · 양 · 이름은
 * `liquids`) · 시료 자리 = 서버 요약 `samples`(자리 · 웰).
 */
export interface LayoutTable {
  columns: readonly { id: string; label: MessageKey }[]
  rows: readonly { key: string; cells: Readonly<Record<string, string>> }[]
}

const commandsOf = (doc: RecipeV2Doc, type: string): Obj[] =>
  (Array.isArray(doc['commands']) ? doc['commands'] : []).filter(
    (c): c is Obj => isObj(c) && c['commandType'] === type,
  )

const paramsOf = (c: Obj): Obj => (isObj(c['params']) ? c['params'] : {})

const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : ''

/** 놓인 곳 — 칸이면 그 칸, 모듈 위면 그 모듈 이름 */
function placeText(location: unknown): string {
  if (!isObj(location)) return ''
  if (typeof location['slotName'] === 'string') return location['slotName']
  if (typeof location['moduleId'] === 'string') return location['moduleId']
  if (typeof location['labwareId'] === 'string') return location['labwareId']
  return ''
}

/** 양 — µL 원값. 1000 이상이면 mL(값을 바꾸지 않고 단위만) */
function volume(ul: unknown): string {
  if (typeof ul !== 'number' || !Number.isFinite(ul)) return ''
  return ul >= 1000 ? `${String(ul / 1000)} mL` : `${String(ul)} µL`
}

/** 데크 3열 자리 이름 — 데크 정의는 Opentrons 슬롯 이름(A3 · B3)을 쓰지만 실물은 칸이 아니라 슬라이더 · 냉장 자리다 */
const COLUMN3_PLACE: Readonly<Record<string, MessageKey>> = {
  A3: 'metric.slider',
  B3: 'tplTable.place.chilled',
}

export function recipeLabwareTable(doc: RecipeV2Doc): LayoutTable {
  /* 모듈 위에 놓인 랩웨어는 그 모듈의 데크 칸으로 적는다(`loadModule.location.slotName` · 셰이커 A3 · 냉장 B3) */
  const moduleSlot = new Map(
    commandsOf(doc, 'loadModule').map((c) => {
      const p = paramsOf(c)
      const location = isObj(p['location']) ? p['location'] : {}
      return [str(p['moduleId']), str(location['slotName'])] as const
    }),
  )
  const slotOf = (location: unknown): string => {
    if (!isObj(location)) return ''
    if (typeof location['slotName'] === 'string') return location['slotName']
    if (typeof location['moduleId'] === 'string') {
      const slot = moduleSlot.get(location['moduleId']) || location['moduleId']
      /* 3열은 칸이 아니다 — 슬라이더 · 냉장 자리다(eric 2026-10-20 「A3은 없어..」) */
      return COLUMN3_PLACE[slot] === undefined ? slot : t(COLUMN3_PLACE[slot])
    }
    return placeText(location)
  }
  /* 칸 차례 — A1 · A2 · A3 · B1 … (줄 글자 → 열 숫자). 칸이 없는 것(폐기통 자리)은 맨 뒤(eric 2026-10-20 「칸 A1, A2, B1, B2, C1, C2 순으로」) */
  const order = (place: string) => {
    const m = /^([A-Z])(\d+)$/u.exec(place)
    return m === null ? [Number.MAX_SAFE_INTEGER, 0] : [m[1]!.charCodeAt(0), Number(m[2])]
  }
  const rows = commandsOf(doc, 'loadLabware').map((c, i) => {
    const p = paramsOf(c)
    return {
      key: str(p['labwareId']) || String(i),
      cells: {
        place: slotOf(p['location']),
        name: str(p['displayName']),
        id: str(p['labwareId']),
      },
    }
  })
  /*
   * 빈 칸도 줄로 — 레시피 데크 칸 표(`instruments.deck_map` 의 키 · A1 … C2) 중 랩웨어가 없는 칸은 랩웨어 칸이 빈 줄(화면이 가운데 「—」)
   * (eric 2026-10-20 「A1, A2는 추가하고 랩웨어에. 비어 있다는 표시로 - 중앙 정렬」)
   */
  const app = isObj(doc['designerApplication']) ? doc['designerApplication'] : {}
  const data = isObj(app['data']) ? app['data'] : {}
  const instruments = isObj(data['instruments']) ? data['instruments'] : {}
  const deckMap = isObj(instruments['deck_map']) ? Object.keys(instruments['deck_map']) : []
  const used = new Set(rows.map((r) => r.cells.place))
  for (const slot of deckMap)
    if (!used.has(slot))
      rows.push({ key: `empty-${slot}`, cells: { place: slot, name: '', id: '' } })
  rows.sort((a, b) => {
    const [ra, ca] = order(a.cells.place)
    const [rb, cb] = order(b.cells.place)
    return ra! - rb! || ca! - cb!
  })
  return {
    /* 「자리」(랩웨어 id)는 내부용이라 열을 세우지 않는다(eric 2026-10-20 「자리의 내용은 내부적으로 쓰는 거라 지워줘」) — 줄 열쇠로만 쓴다 */
    columns: [
      { id: 'place', label: 'tplTable.location' },
      { id: 'name', label: 'tplTable.labware' },
    ],
    rows,
  }
}

export function recipeReagentTable(doc: RecipeV2Doc, sampleRefs: ReadonlySet<string>): LayoutTable {
  const liquids = isObj(doc['liquids']) ? doc['liquids'] : {}
  return {
    columns: [
      { id: 'name', label: 'tplTable.reagent' },
      { id: 'id', label: 'tplTable.labwareId' },
      { id: 'wells', label: 'tplTable.wellsVolume' },
    ],
    rows: commandsOf(doc, 'loadLiquid')
      .map(paramsOf)
      .filter((p) => !sampleRefs.has(str(p['liquidId'])))
      .map((p, i) => {
        const liquid = liquids[str(p['liquidId'])]
        const name = isObj(liquid) ? str(liquid['displayName']) : ''
        const wells = isObj(p['volumeByWell'])
          ? Object.entries(p['volumeByWell'])
              .map(([w, ul]) => [w, volume(ul)].filter((s) => s !== '').join(' '))
              .join(' · ')
          : ''
        return {
          key: `${str(p['liquidId'])}-${str(p['labwareId'])}-${String(i)}`,
          cells: { name: name || str(p['liquidId']), id: str(p['labwareId']), wells },
        }
      }),
  }
}

/** 시약 랙 그림 한 장 — 그 랙(`labwareId`)에 실린 시약. `no` 는 시약 표의 줄 차례(1-기반)라 그림 번호와 표가 같은 수를 말한다 */
export interface ReagentRackView {
  labwareId: string
  /** 레시피가 그 랙에 싣는 Opentrons 정의(`labwareDefinitions[definitionUri]`) — 없으면 그림이 번들 사본으로 선다 */
  definition: Obj | undefined
  /**
   * `amount` — 칸 · 양 글자(eric 2026-10-20 「웰 양을.. 해당 rack에 넣어줘」). 칸마다 양이 같으면 「A1 · A2 · A3 50 mL」, 다르면
   * 「A1 50 mL · B1 15 mL」(시약 표의 웰 · 양 칸과 같은 낱말)
   */
  reagents: readonly {
    no: number
    /** 액체 id(`liquids` 의 키) — 입력 칸 열쇠 */
    id: string
    name: string
    wells: readonly string[]
    amount: string
  }[]
}

/**
 * **시약 배치 그림의 재료** — 시약 표(`recipeReagentTable`)와 같은 `loadLiquid` 줄을 같은 차례로 읽어 랙마다 묶는다
 * (eric 2026-10-20 「이것... 실제 그림으로부터 그려줘」). 랙 차례는 처음 나온 차례다. 시료(트레이에 싣는 액체)는 뺀다.
 */
export function recipeReagentRacks(
  doc: RecipeV2Doc,
  sampleRefs: ReadonlySet<string>,
): ReagentRackView[] {
  const liquids = isObj(doc['liquids']) ? doc['liquids'] : {}
  const racks = new Map<
    string,
    { no: number; id: string; name: string; wells: string[]; amount: string }[]
  >()
  commandsOf(doc, 'loadLiquid')
    .map(paramsOf)
    .filter((p) => !sampleRefs.has(str(p['liquidId'])))
    .forEach((p, i) => {
      const labwareId = str(p['labwareId'])
      if (labwareId === '') return
      const liquid = liquids[str(p['liquidId'])]
      const name = (isObj(liquid) ? str(liquid['displayName']) : '') || str(p['liquidId'])
      const byWell = isObj(p['volumeByWell']) ? Object.entries(p['volumeByWell']) : []
      const wells = byWell.map(([well]) => well)
      const volumes = byWell.map(([, ul]) => volume(ul))
      const list = racks.get(labwareId) ?? []
      const amount =
        volumes.length > 0 && volumes.every((v) => v === volumes[0])
          ? [wells.join(' · '), volumes[0]].filter((x) => x !== '').join(' ')
          : byWell.map(([w], n) => [w, volumes[n]].filter((x) => x !== '').join(' ')).join(' · ')
      list.push({ no: i + 1, id: str(p['liquidId']), name, wells, amount })
      racks.set(labwareId, list)
    })
  return [...racks].map(([labwareId, reagents]) => ({
    labwareId,
    definition: definitionOf(doc, labwareId),
    reagents,
  }))
}

/** 그 랩웨어의 Opentrons 정의 — 편집층 `labware[id].definitionUri` → 문서 `labwareDefinitions` */
function definitionOf(doc: RecipeV2Doc, labwareId: string): Obj | undefined {
  const app = isObj(doc['designerApplication']) ? doc['designerApplication'] : {}
  const data = isObj(app['data']) ? app['data'] : {}
  const labware = isObj(data['labware']) ? data['labware'] : {}
  const entry = labware[labwareId]
  const uri = isObj(entry) ? str(entry['definitionUri']) : ''
  const definitions = isObj(doc['labwareDefinitions']) ? doc['labwareDefinitions'] : {}
  const definition = definitions[uri]
  return isObj(definition) ? definition : undefined
}

/** 시료 자리 이름 — 문서 `liquids[자리].displayName`(「환자 시료 P1」 · 「레퍼런스 REF」). 이름이 없거나 비면 빠진다 */
export function recipeSampleNames(doc: RecipeV2Doc): Record<string, string> {
  const liquids = isObj(doc['liquids']) ? doc['liquids'] : {}
  const out: Record<string, string> = {}
  for (const [id, liquid] of Object.entries(liquids)) {
    const name = isObj(liquid) ? str(liquid['displayName']).trim() : ''
    if (name !== '') out[id] = name
  }
  return out
}

export function recipeSampleTable(
  samples: readonly { sample_ref: string; wells: readonly string[] }[],
): LayoutTable {
  return {
    columns: [
      { id: 'ref', label: 'recipeTemplate.col.sampleRef' },
      { id: 'wells', label: 'column.well' },
    ],
    rows: samples.map((s) => ({
      key: s.sample_ref,
      cells: { ref: s.sample_ref, wells: s.wells.join(', ') },
    })),
  }
}

/**
 * 시료 자리 → 트레이 그림 칸(모니터링 시료 서랍과 같은 `TrayFigure` · eric 2026-10-13 「시료자리는 모니터링의 그림을 이용해서」).
 * 칸 차례 · 이름은 랩웨어 규격(`TRAY_SLOTS` A1 … C3). 자리가 든 칸은 챔버(이름 = 자리 `P1` · `REF`), 나머지는 빈 자리.
 * 🔴 웰 이름이 규격 밖이면 `undefined` — 그림을 짓지 않고 부르는 쪽이 표로 보인다.
 */
export function recipeSampleTrayCells(
  samples: readonly { sample_ref: string; wells: readonly string[] }[],
  slots: readonly string[],
):
  | { slot: string; present: boolean; label?: string; reference?: boolean; group?: number }[]
  | undefined {
  const owner = new Map<string, string>()
  for (const s of samples) for (const w of s.wells) owner.set(w, s.sample_ref)
  /* 묶음 색 번호 — 대조(REF)를 뺀 시료마다 차례로 1 … 4(넘치면 다시 1) · 대조는 색 없이 */
  const groups = new Map<string, number>()
  for (const s of samples)
    if (s.sample_ref !== 'REF' && !groups.has(s.sample_ref))
      groups.set(s.sample_ref, (groups.size % 4) + 1)
  if ([...owner.keys()].some((w) => !slots.includes(w))) return undefined
  return slots.map((slot) => {
    const ref = owner.get(slot)
    return ref === undefined
      ? { slot, present: false }
      : {
          slot,
          present: true,
          label: ref,
          ...(ref === 'REF' ? { reference: true } : {}),
          ...(groups.get(ref) === undefined ? {} : { group: groups.get(ref) as number }),
        }
  })
}
